import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { api } from '../services/api';
import { Button } from '../components/Button';
import { SubscriptionStatus } from '../types';

export const BillingSuccess = () => {
  const navigate = useNavigate();
  const { user, currentCompany, refreshData } = useStore();
  const [statusText, setStatusText] = useState('Confirmando pagamento com o Mercado Pago...');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const confirmPayment = async () => {
      if (!user || !currentCompany?.id) {
        setStatusText('Pagamento recebido. Entre novamente para atualizar sua sessão.');
        return;
      }

      try {
        const response = await api.billing.syncMercadoPagoSubscription(currentCompany.id);
        await refreshData();
        if (!mounted) return;

        if (response?.company?.subscriptionStatus === SubscriptionStatus.ACTIVE || response?.subscriptionStatus === SubscriptionStatus.ACTIVE) {
          setConfirmed(true);
          setStatusText('Assinatura confirmada. Seu FlowTab está liberado.');
          return;
        }

        setStatusText('Pagamento enviado. Se o Mercado Pago ainda estiver processando, a liberação acontece automaticamente em instantes.');
      } catch (error) {
        if (mounted) {
          setStatusText('Pagamento enviado. Não conseguimos confirmar agora, mas o FlowTab vai sincronizar automaticamente.');
        }
      }
    };

    confirmPayment();

    return () => {
      mounted = false;
    };
  }, [user, currentCompany?.id, refreshData]);

  return (
    <div className="min-h-screen bg-slate-950 p-5 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.25),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_34%)]" />
      <main className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center">
        <section className="w-full overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl shadow-emerald-950/30">
          <div className="bg-slate-950 p-8 text-white">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-emerald-200">
              <ShieldCheck className="h-4 w-4" />
              Pagamento seguro FlowTab
            </div>
            <div className="mt-8 flex h-20 w-20 items-center justify-center rounded-[1.7rem] bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-300/20">
              {confirmed ? <CheckCircle className="h-11 w-11" /> : <Loader2 className="h-11 w-11 animate-spin" />}
            </div>
            <h1 className="mt-6 text-3xl font-black tracking-tight lg:text-4xl">
              {confirmed ? 'Pagamento concluído com sucesso' : 'Estamos confirmando sua assinatura'}
            </h1>
            <p className="mt-4 text-sm font-semibold leading-7 text-slate-300">{statusText}</p>
          </div>

          <div className="grid gap-3 bg-white p-6 text-slate-950 sm:grid-cols-2">
            {user ? (
              <Button onClick={() => navigate('/pos')} className="h-14 rounded-2xl font-black">
                Voltar ao PDV
              </Button>
            ) : (
              <Button onClick={() => navigate('/')} className="h-14 rounded-2xl font-black">
                <LogIn className="mr-2 h-5 w-5" />
                Entrar no FlowTab
              </Button>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-14 rounded-2xl bg-slate-100 px-5 text-sm font-black text-slate-700 transition hover:bg-slate-200"
            >
              Verificar novamente
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};
